import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import SvgIcon from '@mui/material/SvgIcon';
import { alpha, styled } from '@mui/material/styles';
import TreeView from '@mui/lab/TreeView';
import TreeItem, { treeItemClasses } from '@mui/lab/TreeItem';
import Collapse from '@mui/material/Collapse';
// web.cjs is required for IE11 support
import { useSpring, animated } from 'react-spring/web.cjs';
import { useState } from 'react';
import { ScaleLoader } from 'react-spinners';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import Button from '@inovua/reactdatagrid-community/packages/Button';
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter'
import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import GridTree from 'react-gridtree';

function MinusSquare(props) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
    </SvgIcon>
  );
}

function PlusSquare(props) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
    </SvgIcon>
  );
}

function CloseSquare(props) {
  return (
    <SvgIcon
      className="close"
      fontSize="inherit"
      style={{ width: 14, height: 14 }}
      {...props}
    >
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M17.485 17.512q-.281.281-.682.281t-.696-.268l-4.12-4.147-4.12 4.147q-.294.268-.696.268t-.682-.281-.281-.682.294-.669l4.12-4.147-4.12-4.147q-.294-.268-.294-.669t.281-.682.682-.281.696 .268l4.12 4.147 4.12-4.147q.294-.268.696-.268t.682.281 .281.669-.294.682l-4.12 4.147 4.12 4.147q.294.268 .294.669t-.281.682zM22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0z" />
    </SvgIcon>
  );
}

function TransitionComponent(props) {
  const style = useSpring({
    from: {
      opacity: 0,
      transform: 'translate3d(20px,0,0)',
    },
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}

TransitionComponent.propTypes = {
  /**
   * Show the component; triggers the enter or exit states
   */
  in: PropTypes.bool,
};

const StyledTreeItem = styled((props) => (
  <TreeItem {...props} TransitionComponent={TransitionComponent} />
))(({ theme }) => ({
  [`& .${treeItemClasses.iconContainer}`]: {
    '& .close': {
      opacity: 0.3,
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

export default function CustomizedTreeView(props) {
  const [statsData, setStatsData] = useState(props.rawcategories)
  const [isLoading, setIsLoading] = useState(false)
  const [gridRef, setGridRef] = useState(null)
  const [namefilter, setNamefilter] = useState('')

  const gridStyle = { height: '400px', marginTop: 10 };

  const filterValue = [
    { name: 'category_name', operator: 'startsWith', type: 'string', value: namefilter },
  ];

  const columns = [
    { name: 'category_name', header: 'Category Name' },
    { name: 'estimated', defaultFlex: 2, header: 'Estimated Budget' },
    { name: 'actual', defaultFlex: 2, header: 'Actual Spent' },
    { name: 'variance', defaultFlex: 2, header: 'Variance', },
  ];

  const exportCSV = () => {
    const columns = gridRef.current.visibleColumns;

    const header = columns.map((c) => c.name).join(',');
    const rows = gridRef.current.data.map((data) => columns.map((c) => data[c.id]).join(','));

    const contents = [header].concat(rows).join('\n');
    const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });

    downloadBlob(blob);
  };

  const downloadBlob = (blob, fileName = 'grid-data.csv') => {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.position = 'absolute';
    link.style.visibility = 'hidden';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };


  const getTreeItemsFromData = treeItems => {
    return treeItems.map(treeItemData => {
      let children = undefined;
      if (treeItemData.children && treeItemData.children.length > 0) {
        children = getTreeItemsFromData(treeItemData.children);
      }
      return (
        <StyledTreeItem
          key={treeItemData.id}
          nodeId={treeItemData.id}
          label={treeItemData.text}
          children={children}
          onClick={() => {
            filterValue[0].value = treeItemData.text
            setNamefilter(treeItemData.text)
            console.log(treeItemData.text)
          }}
        />
      );
    });
  };

  const rowStyle = ({ data }) => {
    let weight = "normal"
    if (data.leaf != '1') weight = "bold"
    return {
      background: data.background,
      color: data.color,
      fontWeight: weight
    }
  }

  return (
    <>
      {props.isLoading ?
        <div className="row mx-0" style={{ height: "100%" }}><ScaleLoader cssOverride={{ "display": "flex", "justifyContent": "center", "alignItems": "center" }} /></div> :


        <div className='row'>
          {/* <div className='col-md-5' style={{ "height": "400px", "overflowY": "scroll" }}>
            <TreeView
              aria-label="customized"
              defaultExpanded={['1']}
              expanded={props.categoryIds}
              defaultCollapseIcon={<MinusSquare />}
              defaultExpandIcon={<PlusSquare />}
              defaultEndIcon={<CloseSquare />}
              sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', width: 'fit-content' }}
            >
              {getTreeItemsFromData(Object.values(props.items))}
            </TreeView>
          </div> */}
          <div className='col-md-12' style={{ "height": "400px", "overflowY": "scroll" }}>
            {
              props.items.length == 0 ? null :
                <GridTree
                  data={props.items}
                  options={{
                    fields: [
                      {
                        property: 'text',
                        displayName: 'Category Name',
                      },
                      {
                        property: 'estimated',
                        displayName: 'Estimated Budget'
                      },
                      {
                        property: 'actual',
                        displayName: 'Actual Spent'
                      },
                      {
                        property: 'variance',
                        displayName: 'Variance',
                        format: (value) => value>100?(<span style={{color:'red'}}>{value}%</span>):(<span style={{color:'green'}}>{value}%</span>)
                      }
                    ]
                  }}
                />}
            {/* {
              statsData == null ? null :
                <ReactDataGrid
                  idProperty="category_id"
                  style={gridStyle}
                  columns={columns}
                  pagination="local"
                  dataSource={props.rawcategories}
                  defaultLimit={25}
                  defaultFilterValue={filterValue}
                  handle={setGridRef}
                  loading={isLoading}
                  rowStyle={rowStyle}
                />
            } */}
            {/* <table className="table verticle-middle table-responsive-md">
              <tbody>
                <tr>
                  <th scope="col">Category Name</th>
                  <td>{data.text}</td>
                </tr>
                {data.leaf == '1' ?
                  <tr>
                    <th scope="col">Estimated Budget</th>
                    <td>{Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(data.estimated_budget)}</td>
                  </tr>
                  : null}
                <tr>
                  <th scope='col'>Actual Spent</th>
                  <td>{Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(props.getSpent(data.id))}</td>
                </tr >
                <tr>
                  <th scope='col'>Variance</th>
                  <td>{variance > 100 ? (<span className='text-danger'>{variance} %</span>) : (<span className='text-success'>{variance} %</span>)}</td>
                </tr >
                <tr>
                  <th scope='col'>Underhead Estimted Budget</th>
                  <td>{Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(props.getSumOfEstimatedBudgets(data.id) - data.estimated_budget)}</td>
                </tr>
              </tbody >
            </table > */}
          </div>
        </div>
      }

    </>
  );
}



